import {
  IProcedureModalityClassifyResult,
  IProcedureModalityRange,
  IProcedureModalityRangeSaveRequest,
} from "types/ProcedureModalityRange/ProcedureModalityRange";

import { api } from ".";
import { PRACTICE_TYPE_EVALUATE_TAG } from "./practiceTypeTags";

/**
 * CLE-3000 — CPT groups: the code ranges behind each procedure type the Service Type rules key on.
 * Eligibility › CPT Groups. SuperAdmin only, every endpoint.
 *
 * CLASSIFY: the tab's tester. A POST that only reads, so it is a QUERY (like the Service Type
 * rules' evaluate), called through the LAZY hook, whose trigger always refetches.
 *
 * Every write invalidates the list, the classify answers and the Service Type rules' evaluate
 * answers (`PRACTICE_TYPE_EVALUATE_TAG`): which group a code is in decides which rule fires, so a
 * cached evaluate answer is stale the moment a range changes. The API's own cache is invalidated
 * by the same save.
 */
const apiWithProcedureModalityRangeTags = api.enhanceEndpoints({
  addTagTypes: ["ProcedureModalityRanges", "PracticeTypeSuggestionRules"],
});

export const PROCEDURE_MODALITY_RANGE_PATH =
  "eligibility/procedure-modality-ranges";

const LIST_TAG = { type: "ProcedureModalityRanges" as const, id: "LIST" };

const WRITE_INVALIDATES = [LIST_TAG, PRACTICE_TYPE_EVALUATE_TAG];

export const procedureModalityRangeQueries =
  apiWithProcedureModalityRangeTags.injectEndpoints({
    endpoints: (builder) => ({
      getProcedureModalityRanges: builder.query<
        IProcedureModalityRange[],
        void
      >({
        query: () => PROCEDURE_MODALITY_RANGE_PATH,
        transformResponse: (response: { data: IProcedureModalityRange[] }) =>
          response.data,
        providesTags: [LIST_TAG],
      }),
      classifyProcedureCodes: builder.query<
        IProcedureModalityClassifyResult,
        string[]
      >({
        query: (codes) => ({
          url: `${PROCEDURE_MODALITY_RANGE_PATH}/classify`,
          method: "POST",
          body: { codes },
        }),
        transformResponse: (response: {
          data: IProcedureModalityClassifyResult;
        }) => response.data,
        providesTags: [LIST_TAG],
      }),
      createProcedureModalityRange: builder.mutation<
        IProcedureModalityRange,
        IProcedureModalityRangeSaveRequest
      >({
        query: (body) => ({
          url: PROCEDURE_MODALITY_RANGE_PATH,
          method: "POST",
          body,
        }),
        transformResponse: (response: { data: IProcedureModalityRange }) =>
          response.data,
        invalidatesTags: WRITE_INVALIDATES,
      }),
      updateProcedureModalityRange: builder.mutation<
        IProcedureModalityRange,
        { id: string; body: IProcedureModalityRangeSaveRequest }
      >({
        query: ({ id, body }) => ({
          url: `${PROCEDURE_MODALITY_RANGE_PATH}/${encodeURIComponent(id)}`,
          method: "PUT",
          body,
        }),
        transformResponse: (response: { data: IProcedureModalityRange }) =>
          response.data,
        invalidatesTags: WRITE_INVALIDATES,
      }),
      deleteProcedureModalityRange: builder.mutation<{ id: string }, string>({
        query: (id) => ({
          url: `${PROCEDURE_MODALITY_RANGE_PATH}/${encodeURIComponent(id)}`,
          method: "DELETE",
        }),
        transformResponse: (response: { data: { id: string } }) =>
          response.data,
        invalidatesTags: WRITE_INVALIDATES,
      }),
    }),
  });

export const {
  useGetProcedureModalityRangesQuery,
  useLazyClassifyProcedureCodesQuery,
  useCreateProcedureModalityRangeMutation,
  useUpdateProcedureModalityRangeMutation,
  useDeleteProcedureModalityRangeMutation,
} = procedureModalityRangeQueries;
